import { useScrollReveal } from "@/hooks/useScrollReveal";

const TEAM_MEMBERS = [
  { name: "Ana López", role: "Encargada Convivencia", icon: "ri-shield-user-line", color: "bg-primary-500", cases: 12, interventions: 34 },
  { name: "Carlos Reyes", role: "Orientador", icon: "ri-compass-3-line", color: "bg-accent-500", cases: 8, interventions: 21 },
  { name: "María García", role: "Psicóloga", icon: "ri-psychotherapy-line", color: "bg-secondary-500", cases: 6, interventions: 28 },
  { name: "Pedro Sandoval", role: "Inspector General", icon: "ri-police-car-line", color: "bg-primary-500", cases: 5, interventions: 16 },
];

const DERIVATIONS = [
  { from: "Convivencia", to: "Psicología", student: "Emilia Rojas", reason: "Evaluación socioemocional", status: "Pendiente", statusColor: "bg-amber-100 text-amber-700", date: "Hace 2 días" },
  { from: "Orientación", to: "Convivencia", student: "Benjamín Cáceres", reason: "Seguimiento de medidas", status: "Aceptada", statusColor: "bg-primary-100 text-primary-700", date: "Hace 5 días" },
  { from: "Psicología", to: "Externo", student: "Martín Soto", reason: "Derivación a especialista", status: "En proceso", statusColor: "bg-accent-100 text-accent-700", date: "Ayer" },
  { from: "Inspectoría", to: "Convivencia", student: "Isidora Muñoz", reason: "Reporte de incidente", status: "Completada", statusColor: "bg-secondary-100 text-secondary-700", date: "Hace 1 semana" },
];

const INTERVENTION_STATS = [
  { label: "Intervenciones semanales", value: "47", icon: "ri-heart-pulse-line", color: "bg-accent-500" },
  { label: "Entrevistas realizadas", value: "23", icon: "ri-mic-line", color: "bg-primary-500" },
  { label: "Derivaciones activas", value: "8", icon: "ri-share-forward-line", color: "bg-secondary-500" },
  { label: "Tasa colaboración", value: "92%", icon: "ri-team-line", color: "bg-emerald-500" },
];

export default function EquiposPsicosocialesSection() {
  const sectionRef = useScrollReveal<HTMLElement>({ threshold: 0.12, selector: ".scroll-reveal", staggerDelay: 100 });

  return (
    <section id="equipos-psicosociales" className="py-20 md:py-28 bg-background-100 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-background-300/60 to-transparent" />

      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12 md:mb-16">
          <span className="scroll-reveal text-xs font-semibold text-accent-600 tracking-widest uppercase bg-accent-50 px-3 py-1 rounded-full inline-block">
            Equipos Psicosociales
          </span>
          <h2 className="scroll-reveal text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mt-4 tracking-tight">
            Trabajo interdisciplinario coordinado
          </h2>
          <p className="scroll-reveal text-lg text-foreground-500 mt-4 max-w-2xl mx-auto leading-relaxed">
            Conecta a orientadores, psicólogos, encargados de convivencia e inspectores en un solo ecosistema. Cada profesional aporta desde su rol con trazabilidad total.
          </p>
        </div>

        {/* Team Overview */}
        <div className="scroll-reveal mb-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {TEAM_MEMBERS.map((member) => (
              <div key={member.name} className="bg-white rounded-2xl border border-background-200/80 shadow-soft p-5 hover:shadow-soft-md transition-all duration-300 cursor-default group">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-xl ${member.color} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                    <i className={`${member.icon} text-white text-base`} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-foreground-900">{member.name}</p>
                    <p className="text-xs text-foreground-400">{member.role}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-background-50 rounded-xl p-2 text-center">
                    <p className="text-lg font-extrabold text-foreground-900">{member.cases}</p>
                    <p className="text-[10px] text-foreground-400">Casos</p>
                  </div>
                  <div className="bg-background-50 rounded-xl p-2 text-center">
                    <p className="text-lg font-extrabold text-foreground-900">{member.interventions}</p>
                    <p className="text-[10px] text-foreground-400">Intervenciones</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Derivations flow + Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Derivations table */}
          <div className="lg:col-span-2 scroll-reveal">
            <div className="bg-white rounded-3xl border border-background-200/80 shadow-soft-lg p-6 md:p-8">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-bold text-foreground-950">Derivaciones recientes</h3>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-accent-500" />
                  <span className="text-xs text-foreground-400">8 activas</span>
                </div>
              </div>

              <div className="space-y-3">
                {DERIVATIONS.map((d) => (
                  <div key={`${d.student}-${d.from}-${d.to}`} className="group flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-2xl bg-background-50/80 hover:bg-background-100/80 transition-all duration-200 cursor-pointer border border-transparent hover:border-background-200">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <span className="text-xs font-semibold text-foreground-700 bg-background-200 px-2 py-0.5 rounded-lg">{d.from}</span>
                        <i className="ri-arrow-right-line text-foreground-300 text-xs" />
                        <span className="text-xs font-semibold text-foreground-700 bg-background-200 px-2 py-0.5 rounded-lg">{d.to}</span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-foreground-900">{d.student}</p>
                        <p className="text-xs text-foreground-400">{d.reason}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${d.statusColor}`}>{d.status}</span>
                      <span className="text-xs text-foreground-400">{d.date}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Stats panel */}
          <div className="flex flex-col gap-6 scroll-reveal">
            <div className="bg-white rounded-2xl border border-background-200/80 shadow-soft p-5">
              <h4 className="text-sm font-bold text-foreground-800 mb-4">Actividad del equipo</h4>
              <div className="space-y-4">
                {INTERVENTION_STATS.map((stat) => (
                  <div key={stat.label} className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-xl ${stat.color} flex items-center justify-center flex-shrink-0`}>
                      <i className={`${stat.icon} text-white text-sm`} />
                    </div>
                    <div>
                      <p className="text-lg font-extrabold text-foreground-950">{stat.value}</p>
                      <p className="text-xs text-foreground-400">{stat.label}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Collaboration highlight */}
            <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-2xl shadow-soft-lg p-6 text-white">
              <h4 className="text-sm font-bold text-white/80 mb-3">Colaboración interdisciplinaria</h4>
              <p className="text-xs text-white/70 leading-relaxed mb-3">
                Los casos con intervención de 2+ profesionales tienen un <strong className="text-white">92%</strong> más de probabilidad de resolución efectiva.
              </p>
              <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                <div className="h-full bg-white rounded-full animate-kpi-bar" style={{ width: "92%" }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}